import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  RefreshCw,
  Briefcase,
  MapPin,
  Clock,
  Calendar,
  AlertCircle,
  EyeOff,
} from 'lucide-react'
import matchingService from '@/services/matchingService'
import recruiterJobService from '@/services/recruiterJobService'
import jobProspectService from '@/services/jobProspectService'
import { toast } from '@/stores/toastStore'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import QuotaBar from '@/components/recruiter/QuotaBar'
import { MatchResultCard } from '@/components/recruiter/ai/MatchResultCard'
import { useSubmitFeedback, useArchiveMatch, useDeleteMatch } from '@/hooks/useAIMatching'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import type { AIMatchSummary, AIMatchResult, RecruiterJob, EmploymentType, WorkMode } from '@/types'

const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contract',
  INTERNSHIP: 'Internship',
}

const WORK_MODE_LABELS: Record<WorkMode, string> = {
  REMOTE: 'Remote',
  HYBRID: 'Hybrid',
  ONSITE: 'Onsite',
}

interface SearchBatch {
  match: AIMatchSummary
  results: AIMatchResult[]
}

export default function AIMatchJobCandidates() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()

  const [job, setJob] = useState<RecruiterJob | null>(null)
  const [batches, setBatches] = useState<SearchBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [excludeProspects, setExcludeProspects] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean
    matchResultId: string | null
  }>({ isOpen: false, matchResultId: null })

  const submitFeedbackMutation = useSubmitFeedback()
  const archiveMatchMutation = useArchiveMatch()
  const deleteMatchMutation = useDeleteMatch()

  const loadData = useCallback(async () => {
    if (!jobId) return
    setLoading(true)
    try {
      // Load job info and all matches in parallel
      const [jobData, matchesResponse] = await Promise.all([
        recruiterJobService.getJob(jobId),
        matchingService.listMatches({ limit: 50 }),
      ])

      setJob(jobData)

      // Filter matches for this job and sort newest first
      const jobMatches = matchesResponse.matches
        .filter((m) => m.recruiterJobId === jobId)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

      // Fetch results for each completed match
      const completedMatches = jobMatches.filter(
        (m) =>
          m.status === 'completed' &&
          m.errorCode !== 'NO_CANDIDATES_FOUND' &&
          (m.totalMatches ?? 0) > 0
      )

      const batchResults = await Promise.all(
        completedMatches.map(async (match) => {
          try {
            const response = await matchingService.getMatchResults({
              matchId: match.id,
              page: 1,
              limit: 50,
              minScore: 0,
              excludeProspects,
            })
            return { match, results: response.results } as SearchBatch
          } catch {
            return { match, results: [] } as SearchBatch
          }
        })
      )

      setBatches(batchResults)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load candidates')
      if (error.response?.status === 404) {
        navigate('/recruiter/ai-matching')
      }
    } finally {
      setLoading(false)
    }
  }, [jobId, excludeProspects, navigate])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleFeedback = async (
    matchId: string,
    candidateId: string,
    feedback: 'RELEVANT' | 'NOT_RELEVANT' | 'SHORTLISTED',
    notes?: string
  ) => {
    try {
      await submitFeedbackMutation.mutateAsync({ matchId, candidateId, feedback, notes })
      if (feedback === 'SHORTLISTED') {
        toast.success('Candidate shortlisted', 4000)
      } else {
        toast.success('Feedback saved')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save feedback')
    }
  }

  const handleArchive = async (matchResultId: string) => {
    try {
      await archiveMatchMutation.mutateAsync(matchResultId)
      toast.success('Candidate archived')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to archive')
    }
  }

  const handleDeleteClick = (matchResultId: string) => {
    setDeleteConfirmation({ isOpen: true, matchResultId })
  }

  const handleDeleteConfirm = async () => {
    const matchResultId = deleteConfirmation.matchResultId
    if (!matchResultId) return
    try {
      await deleteMatchMutation.mutateAsync(matchResultId)
      toast.success('Candidate removed')
      setDeleteConfirmation({ isOpen: false, matchResultId: null })
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete')
    }
  }

  const handleAddToProspects = async (candidateId: string, aiMatchResultId: string) => {
    if (!jobId) return
    try {
      await jobProspectService.addProspect({
        candidateId,
        recruiterJobId: jobId,
        source: 'AI_MATCH',
        aiMatchResultId,
      })
      toast.success('Candidate added to prospects')
    } catch (error: any) {
      const msg =
        error.response?.data?.error || error.response?.data?.message || 'Failed to add prospect'
      toast.error(msg)
      throw error
    }
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const totalCandidates = batches.reduce((sum, b) => sum + b.results.length, 0)

  return (
    <>
      <QuotaBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <Button
          onClick={() => navigate('/recruiter/ai-matching')}
          variant="secondary"
          className="mb-6 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to AI Matching
        </Button>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-blue-600" />
              {job?.title ?? 'AI Match Candidates'}
            </h1>
            {job && (
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                {job.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {job.location}
                  </span>
                )}
                {job.employmentType && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                    {EMPLOYMENT_TYPE_LABELS[job.employmentType] ?? job.employmentType}
                  </span>
                )}
                {job.workMode && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                    {WORK_MODE_LABELS[job.workMode] ?? job.workMode}
                  </span>
                )}
                {job.extractedExperienceYears != null && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {job.extractedExperienceYears}+ yrs experience
                  </span>
                )}
              </div>
            )}
            {job?.extractedSkills && job.extractedSkills.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {job.extractedSkills.slice(0, 8).map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 rounded"
                  >
                    {skill}
                  </span>
                ))}
                {job.extractedSkills.length > 8 && (
                  <span className="px-2 py-0.5 text-xs text-gray-400">
                    +{job.extractedSkills.length - 8} more
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setExcludeProspects(!excludeProspects)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium border transition-colors rounded ${
                excludeProspects
                  ? 'bg-purple-600 text-white border-purple-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <EyeOff className="w-4 h-4" />
              Hide prospects
            </button>
            <Button
              onClick={() => navigate(`/recruiter/ai-matching/create?jobId=${jobId}`)}
              variant="primary"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              New Search
            </Button>
            {job && (
              <Button
                onClick={() => navigate(`/recruiter/jobs/${job.id}`)}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <Briefcase className="w-4 h-4" />
                View Job
              </Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        )}

        {!loading && batches.length === 0 && (
          <Card className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No candidates found yet</h3>
            <p className="text-gray-600 mb-6">
              Run an AI search to find matching candidates for this job.
            </p>
            <Button
              onClick={() => navigate(`/recruiter/ai-matching/create?jobId=${jobId}`)}
              variant="primary"
              className="inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Run AI Search
            </Button>
          </Card>
        )}

        {!loading && batches.length > 0 && (
          <div className="space-y-8">
            {/* Summary */}
            <p className="text-sm text-gray-500">
              {totalCandidates} candidate{totalCandidates !== 1 ? 's' : ''} across {batches.length}{' '}
              search{batches.length !== 1 ? 'es' : ''}
            </p>

            {/* Batches — newest first */}
            {batches.map((batch, batchIndex) => (
              <div key={batch.match.id}>
                {/* Search label */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <Calendar className="w-3.5 h-3.5" />
                    Search {batches.length - batchIndex} — {formatDate(batch.match.createdAt)}
                  </div>
                  {batchIndex === 0 && (
                    <span className="text-xs font-medium px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                      Latest
                    </span>
                  )}
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">
                    {batch.results.length} candidate{batch.results.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Candidates */}
                {batch.results.length > 0 ? (
                  <div className="space-y-4">
                    {batch.results.map((result, index) => (
                      <MatchResultCard
                        key={result.id}
                        result={result}
                        rank={result.rankPosition || index + 1}
                        onFeedback={async (feedback, notes) => {
                          await handleFeedback(batch.match.id, result.candidateId, feedback, notes)
                        }}
                        onArchive={async () => {
                          await handleArchive(result.id)
                        }}
                        onDelete={async () => {
                          handleDeleteClick(result.id)
                        }}
                        onAddToProspects={handleAddToProspects}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic py-2">
                    No candidates to show for this search.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <ConfirmationModal
          isOpen={deleteConfirmation.isOpen}
          onClose={() => setDeleteConfirmation({ isOpen: false, matchResultId: null })}
          onConfirm={handleDeleteConfirm}
          title="Remove Candidate"
          message="Are you sure you want to remove this candidate from your results?"
          confirmText="Remove"
          cancelText="Cancel"
          isDestructive={true}
          isLoading={deleteMatchMutation.isPending}
        />
      </div>
    </>
  )
}
